import React, { ChangeEvent, Dispatch, SetStateAction, useState } from "react"
import tw from "twin.macro"
import { useTranslation } from "next-i18next"
import { Button, ButtonTheme } from "../formElements/button"
import { useSendVerifyEmailOtpMutation, useVerifyEmailOtpMutation } from "../../graphql/generated/graphql"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"
import { Input, InputType } from "../formElements/input"
import { Digit, StyledSpinner, VerifiedState } from "./verifyPhone"
import { JoinStep } from "../authentication/join"

interface VerifyEmailProps {
  email: string
  setJoinStep?: Dispatch<SetStateAction<JoinStep>>
}

// NOTE: This is the "original" magic link verification component which is currently only used for sign up.
// TODO: Need to refactor this and the `VerifyNewEmail` and `VerifyPhone` components
export const VerifyEmail = (props: VerifyEmailProps) => {
  const { email, setJoinStep } = props
  const { t } = useTranslation("verification")
  const [code, setCode] = useState(["", "", "", "", "", ""])
  const [verifiedState, setVerifiedState] = useState<string>(VerifiedState.Default)
  const [verifyEmailOtp] = useVerifyEmailOtpMutation()
  const [sendVerifyEmailOtp] = useSendVerifyEmailOtpMutation()
  const disabled = verifiedState === VerifiedState.Processing || verifiedState === VerifiedState.Success
  const invalid = verifiedState === VerifiedState.Invalid

  const handleChange = (e: ChangeEvent<HTMLInputElement>, i: number) => {
    const { value } = e.target
    setCode(
      code.map((v, j) => {
        if (i === j) {
          v = value
        }
        return v
      }),
    )
    if (value) {
      const next: HTMLInputElement | null = document.querySelector(`#code${i + 1}-id`)
      next && next.focus()
    }
  }
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    const key = e.key
    if (key === "Backspace" || key === "Delete") {
      setCode(
        code.map((v, j) => {
          if (i === j) {
            v = ""
          }
          return v
        }),
      )
      const next: HTMLInputElement | null = document.querySelector(`#code${i - 1}-id`)
      next && next.focus()
    }
  }
  const handlePaste = () => {
    document.addEventListener("paste", (e) => {
      const data = e.clipboardData?.getData("Text").slice(0, 6).split("")
      data && setCode(data)
    })
  }
  const verifyEmail = async () => {
    const emailVerified = await verifyEmailOtp({ variables: { code: code.join(""), email } })
    if (emailVerified.data?.MagicSignup?.success) {
      setVerifiedState(VerifiedState.Success)
      const timer = setTimeout(() => {
        setJoinStep?.(JoinStep.CreateAccount)
        clearTimeout(timer)
      }, 1000)
    } else {
      verifyFailed()
    }
  }
  const verifyFailed = () => {
    setVerifiedState(VerifiedState.Invalid)
    setCode(["", "", "", "", "", ""])
  }
  const handleResendCode = async () => {
    await sendVerifyEmailOtp({ variables: { email } })
  }

  useEffectAsync(async () => {
    if (code.join("").length === 6) {
      setVerifiedState(VerifiedState.Processing)
      await verifyEmail()
    }
    handlePaste()
  }, [code])

  return (
    <div tw="mobile-lg:(w-[260px])">
      <form name="verify-email-otp" autoComplete="off" tw="relative space-y-4 flex flex-col items-center text-center">
        <h3 tw="title-4">{t("verify_email.title")}</h3>
        <div tw="flex flex-row flex-wrap justify-center space-x-1">
          {code.map((digit, i) => (
            <Digit key={i} invalid={invalid}>
              <Input
                name={`code${i}`}
                inputValue={digit}
                type={InputType.Number}
                autoFocus={i === 0}
                maxLength={1}
                handleChange={(e) => handleChange(e, i)}
                handleKeyDown={(e) => void handleKeyDown(e, i)}
                invalid={invalid}
                disabled={disabled}
              />
            </Digit>
          ))}
          <div tw="w-full">
            <p tw="h-7 my-1 text-sm">
              {verifiedState == VerifiedState.Success && <span tw="text-green">{t("verify_email.success")}</span>}
              {verifiedState == VerifiedState.Invalid && <span tw="text-red">{t("error_invalid_code")}</span>}
            </p>
            <p>{t("verify_email.description")}</p>
            <span tw="font-bold">{email}</span>
            <p>{t("verify_email.note")}</p>
          </div>
        </div>

        <div tw="space-x-2">
          <div
            tw="relative w-full flex justify-center invisible"
            css={[verifiedState == VerifiedState.Processing && tw`visible`]}
          >
            <StyledSpinner />
          </div>
          <div tw="pb-8">
            <Button
              action={t("resend_code_cta")}
              theme={ButtonTheme.PrimaryGhost}
              isDisabled={disabled}
              onClickAsync={handleResendCode}
            />
          </div>
        </div>
      </form>
    </div>
  )
}
